import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  Divider,
} from "@mui/material";
import { IconChevronLeft, IconScan } from "@tabler/icons-react";
import { assembliesApi } from "../api";
import { cardArticleSubline, cardHeadingTitle } from "../utils/assemblyCardText";
import {
  bannerSx,
  bannerTextSx,
  barIconSx,
  cardSx,
  ds,
  dsText,
  sectionLabelSx,
  shellSx,
  topBarSx,
} from "../theme/keeperTheme";


export default function AssembliesPage() {
  const { registerToolbarRefresh } = useOutletContext() || {};
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [assemblyOrdersCount, setAssemblyOrdersCount] = useState(0);
  const [assemblyOrdersByCabinet, setAssemblyOrdersByCabinet] = useState([]);
  const [missingStickersCount, setMissingStickersCount] = useState(0);
  const [selectedCabinetId, setSelectedCabinetId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await assembliesApi.onAssembly();
      setItems(Array.isArray(data.items) ? data.items : []);
      setAssemblyOrdersCount(
        typeof data.assembly_orders_count === "number" ? data.assembly_orders_count : 0,
      );
      setAssemblyOrdersByCabinet(
        Array.isArray(data.assembly_orders_by_cabinet) ? data.assembly_orders_by_cabinet : [],
      );
      setMissingStickersCount(
        typeof data.missing_stickers_count === "number" ? data.missing_stickers_count : 0,
      );
      setSelectedCabinetId((prev) => {
        const cabinets = Array.isArray(data.assembly_orders_by_cabinet)
          ? data.assembly_orders_by_cabinet
          : [];
        if (cabinets.length === 0) return null;
        const exists = cabinets.some((c) => String(c.connection_id) === String(prev));
        if (prev != null && exists) return prev;
        return null;
      });
    } catch (e) {
      setError(e?.message || "Ошибка загрузки");
      setMissingStickersCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const selectedCabinetItems =
    selectedCabinetId == null
      ? []
      : items
          .map((row) => {
            const byCabinet = Array.isArray(row.by_cabinet) ? row.by_cabinet : [];
            const selected = byCabinet.find(
              (c) => String(c.connection_id) === String(selectedCabinetId),
            );
            const qty = Number(selected?.qty) || 0;
            if (qty <= 0) return null;
            return { ...row, selectedCabinetQty: qty };
          })
          .filter(Boolean);
  const selectedCabinet = assemblyOrdersByCabinet.find(
    (c) => String(c.connection_id) === String(selectedCabinetId),
  );
  const selectedCabinetName =
    (selectedCabinet?.name && String(selectedCabinet.name).trim()) ||
    (selectedCabinet?.connection_id != null
      ? `Кабинет ${selectedCabinet.connection_id}`
      : "");

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!registerToolbarRefresh) return undefined;
    return registerToolbarRefresh(() => loadData());
  }, [registerToolbarRefresh, loadData]);

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          ...topBarSx,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
        }}
      >
        <IconButton
          onClick={() => navigate("/pick", { replace: false })}
          aria-label="Назад к выбору маркетплейса"
          sx={barIconSx}
        >
          <IconChevronLeft size={26} stroke={1.5} />
        </IconButton>
        <Typography sx={{ color: ds.text, fontWeight: 700, flex: 1 }}>
          На сборке
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            if (selectedCabinetId == null || starting) return;
            setStarting(true);
            setError(null);
            try {
              await assembliesApi.start({ connection_id: Number(selectedCabinetId) });
              const q = new URLSearchParams({
                cabinet: String(selectedCabinetId),
                cabinet_name: selectedCabinetName,
              });
              navigate(`/assemblies/start?${q.toString()}`);
            } catch (e) {
              setError(e?.message || "Не удалось начать сборку");
            } finally {
              setStarting(false);
            }
          }}
          disabled={selectedCabinetId == null || starting}
          startIcon={
            starting ? (
              <CircularProgress size={18} sx={{ color: "inherit" }} />
            ) : (
              <IconScan size={20} stroke={1.75} />
            )
          }
          sx={{
            flexShrink: 0,
            mr: 1.25,
            px: 2,
            py: 0.85,
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            letterSpacing: "0.02em",
            boxShadow: ds.shadowXs,
            bgcolor: ds.primary,
            "&:hover": { bgcolor: ds.primaryHover, boxShadow: ds.shadowMd },
          }}
        >
          {starting ? "Подготовка…" : "Начать"}
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          px: 2,
          py: 1.5,
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={36} sx={{ color: ds.primary }} />
          </Box>
        )}
        {!loading && error && (
          <Typography sx={{ color: dsText.danger, textAlign: "center", py: 4 }}>
            {error}
          </Typography>
        )}
        {!loading && !error && missingStickersCount > 0 && (
          <Box
            sx={{ ...bannerSx("warning"), mb: 1.5 }}
          >
            <Typography sx={{ ...bannerTextSx, fontWeight: 500 }}>
              У {missingStickersCount}{" "}
              {missingStickersCount === 1 ? "сборочного" : "сборочных"} на сборке ещё нет
              стикера WB. Скан QR стикера может не сработать — дождитесь синхронизации.
            </Typography>
          </Box>
        )}
        {!loading && !error && items.length === 0 && (
          <Typography sx={{ color: ds.text2, textAlign: "center", py: 6 }}>
            Нет заказов на сборке
          </Typography>
        )}
        {!loading && !error && assemblyOrdersByCabinet.length > 0 && (
          <Box
            sx={{ ...cardSx, mb: 1.5, py: 1.4, px: 1.6 }}
          >
            <Typography sx={{ color: ds.text, fontWeight: 700, fontSize: "0.9375rem", lineHeight: 1.35 }}>
              Сборки по кабинетам
            </Typography>
            <Divider sx={{ borderColor: ds.border, my: 1.25 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
              {assemblyOrdersByCabinet.map((c) => {
                const label =
                  (c.name && String(c.name).trim()) ||
                  (c.connection_id != null ? `Кабинет ${c.connection_id}` : "Кабинет");
                const active = String(selectedCabinetId) === String(c.connection_id);
                return (
                  <Box
                    key={String(c.connection_id ?? label)}
                    onClick={() => setSelectedCabinetId(c.connection_id ?? null)}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 1.25,
                      minWidth: 0,
                      borderRadius: `${ds.radius}px`,
                      minHeight: 44,
                      px: 1.25,
                      py: 0.9,
                      cursor: "pointer",
                      bgcolor: active ? ds.primarySoft : ds.surface2,
                      border: `1px solid ${active ? ds.primary : ds.border}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: active ? ds.text : ds.text2,
                        fontSize: "0.78rem",
                        lineHeight: 1.4,
                        fontWeight: 600,
                        minWidth: 0,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={label}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        color: active ? ds.primaryHover : ds.text,
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        fontFeatureSettings: '"tnum"',
                        flexShrink: 0,
                      }}
                      title="Сборочных заданий по этому кабинету"
                    >
                      {typeof c.count === "number" ? c.count : 0}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ ...sectionLabelSx, mt: 1.25, fontWeight: 600 }}>
              Всего по всем кабинетам: {assemblyOrdersCount}
            </Typography>
          </Box>
        )}
        {!loading &&
          !error &&
          assemblyOrdersByCabinet.length > 0 &&
          selectedCabinetId == null && (
            <Typography sx={{ color: ds.text2, textAlign: "center", py: 2 }}>
              Выберите кабинет, затем нажмите «Начать».
            </Typography>
          )}
        {!loading &&
          !error &&
          assemblyOrdersByCabinet.length > 0 &&
          selectedCabinetId != null &&
          selectedCabinetItems.length === 0 && (
            <Typography sx={{ color: ds.text2, textAlign: "center", py: 4 }}>
              Нет заданий в выбранном кабинете
            </Typography>
          )}
        {!loading &&
          !error &&
          selectedCabinetItems.map((row) => {
            const heading = cardHeadingTitle(row.title, row.supplier_article);
            const articleLine = cardArticleSubline(row.title, row.supplier_article);
            return (
            <Box
              key={row.nm_id}
              sx={{ ...cardSx, mb: 1, py: 1.2, px: 1.4 }}
            >
              <Typography
                sx={{
                  color: ds.text,
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  lineHeight: 1.3,
                  mb: articleLine ? 0.35 : 0.25,
                }}
              >
                {heading}
              </Typography>
              {articleLine ? (
                <Typography
                  sx={{ color: ds.text2, fontSize: "0.75rem", fontWeight: 600, mb: 0.35, lineHeight: 1.35 }}
                >
                  {articleLine}
                </Typography>
              ) : null}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.45, mt: 0.2 }}>
                <Box
                  sx={{
                    alignSelf: "flex-start",
                    px: 1.1,
                    py: 0.4,
                    borderRadius: 999,
                    bgcolor: ds.primarySoft,
                    border: `1px solid ${ds.primarySoft2}`,
                  }}
                >
                  <Typography
                    sx={{
                      color: ds.primaryHover,
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      lineHeight: 1.25,
                      letterSpacing: "0.02em",
                    }}
                  >
                    {row.selectedCabinetQty ?? row.total_qty} шт.
                  </Typography>
                </Box>
                {Array.isArray(row.by_cabinet) && row.by_cabinet.length > 1 ? (
                  <Typography
                    sx={{
                      color: ds.text2,
                      fontSize: "0.7rem",
                      lineHeight: 1.45,
                      fontWeight: 500,
                    }}
                  >
                    {row.by_cabinet
                      .map((c, i) => `каб ${i + 1} - ${c.qty}`)
                      .join("\u00a0\u00a0")}
                  </Typography>
                ) : null}
              </Box>
            </Box>
            );
          })}
      </Box>
    </Box>
  );
}
